// Real AdminPort: the /api/v1/admin JSON API (same-origin session cookie + X-CSRF-Token via api()).
// Only these endpoints are called; none returns notes. Task titles appear only in a person's latest activity on the
// usage page (owner decision 2026-10-13, design Admin Panel usage).
import { api } from '@/api/client';
import { TransportError } from '@/app/ports';
import { AdminError, type AdminPort } from './ports';

const BASE = '/admin';

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    return await api<T>(method, BASE + path, body);
  } catch (e) {
    if (e instanceof TransportError) {
      const kind = e.kind === 'auth' ? 'auth' : e.kind === 'forbidden' ? 'forbidden' : e.kind === 'network' ? 'network' : e.kind === 'server' ? 'server' : 'rejected';
      throw new AdminError(kind, e.message, e.code);
    }
    throw new AdminError('server', (e as Error).message || 'error');
  }
}

const qs = (o: Record<string, string | number | null | undefined>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? '?' + s : '';
};
const seg = (id: string) => '/' + encodeURIComponent(id);

export function httpAdminPort(): AdminPort {
  return {
    demo: false,
    me: () => call('GET', '/me'),
    overview: range => call('GET', '/overview' + qs({ range })),
    users: (q = {}) => call('GET', '/users' + qs({ q: q.q, limit: q.limit, cursor: q.cursor })),
    user: id => call('GET', '/users' + seg(id)),
    usage: (range, tz) => call('GET', '/usage' + qs({ range, tz })),
    userUsage: (id, range, tz) => call('GET', '/users' + seg(id) + '/usage' + qs({ range, tz })),
    setUserStatus: (id, status) => call('PATCH', '/users' + seg(id), { status }),
    signOutUser: id => call('POST', '/users' + seg(id) + '/signout'),
    setStaffRole: (userId, role) => call('PUT', '/staff' + seg(userId), { role }),
    removeStaff: userId => call('DELETE', '/staff' + seg(userId)),
    userInvitations: () => call('GET', '/user-invitations'),
    inviteUser: (email, role) => call('POST', '/user-invitations', { email, role }),
    cancelUserInvitation: id => call('DELETE', '/user-invitations' + seg(id)),
    projects: () => call('GET', '/projects'),
    links: () => call('GET', '/links'),
    setLinkActive: (id, active) => call('POST', '/links' + seg(id) + (active ? '/restore' : '/revoke')),
    invitations: () => call('GET', '/invitations'),
    revokeInvitation: id => call('DELETE', '/invitations' + seg(id)),
    audit: (q = {}) => call('GET', '/audit' + qs({ type: q.type === 'all' ? null : q.type, limit: q.limit, cursor: q.cursor })),
    releases: () => call('GET', '/releases'),
    setReleaseHidden: (version, hidden) => call('PUT', '/releases/builtin' + seg(version), { hidden }),
    trash: () => call('GET', '/trash'),
    settings: () => call('GET', '/settings'),
    saveSettings: patch => call('PUT', '/settings', patch),
    mail: () => call('GET', '/mail'),
    testMail: () => call('POST', '/mail/test'),
    notifSummary: () => call('GET', '/notifications/summary'),
    deleteUser: id => call('DELETE', '/users' + seg(id)),
    restoreUser: id => call('POST', '/users' + seg(id) + '/restore'),
    sendPasswordReset: id => call('POST', '/users' + seg(id) + '/password-reset'),
    emailUsers: (user_ids, subject, body) => call('POST', '/users/email', { user_ids, subject, body }),
    projectMembers: id => call('GET', '/projects' + seg(id) + '/members'),
    transferProject: (id, user_id) => call('POST', '/projects' + seg(id) + '/owner', { user_id }),
    trashProject: id => call('DELETE', '/projects' + seg(id)),
    restoreTrash: (kind, id) => call('POST', '/trash/' + kind + seg(id) + '/restore'),
    purgeTrash: (kind, id) => call('DELETE', '/trash/' + kind + seg(id)),
    emptyTrash: () => call('DELETE', '/trash'),
    resendInvitation: id => call('POST', '/invitations' + seg(id) + '/resend'),
    send2fa: () => call('POST', '/2fa/send'),
    verify2fa: code => call('POST', '/2fa/verify', { code }),
    backups: () => call('GET', '/backups'),
    backupNow: () => call('POST', '/backups'),
    backupJob: id => call('GET', '/backups/jobs' + seg(id)),
    backupSchedule: (everyHours, at) => call('PUT', '/backups/schedule', { every_hours: everyHours, at }),
    backupDownload: (id, password) => call('POST', '/backups' + seg(id) + '/download', { password }),
    backupRestore: (id, password, confirm) => call('POST', '/backups' + seg(id) + '/restore', { password, confirm }),
    exportAuditUrl: type => '/api/v1' + BASE + '/export/audit.csv' + qs({ type: type === 'all' ? null : type }),
  };
}
